import { DRAFT_BRANCH_ROUTE_PREFIX } from "../reservedRoutes";
import { draftRouteAuth } from "../stepConfig";

// The resource identifier (RFC 8707 / RFC 9728) is the canonical external URL
// of a route: the public origin plus the route path, no query or fragment. The
// protected-resource-metadata endpoint and the per-request audience check must
// compute it identically, so both go through here.
export function canonicalResourceUrl(
  externalBaseUrl: string,
  pathname: string
): string {
  const origin = new URL(externalBaseUrl).origin;
  return `${origin}${normalizeRoutePath(pathname)}`;
}

export const PROTECTED_RESOURCE_METADATA_PREFIX =
  "/.well-known/oauth-protected-resource";

// RFC 9728 inserts the resource path after the well-known segment, so the
// metadata for `https://host/mcp` lives at
// `https://host/.well-known/oauth-protected-resource/mcp`.
export function protectedResourceMetadataUrl(
  externalBaseUrl: string,
  pathname: string
): string {
  const origin = new URL(externalBaseUrl).origin;
  return `${origin}${PROTECTED_RESOURCE_METADATA_PREFIX}${normalizeRoutePath(pathname)}`;
}

export function normalizeRoutePath(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

export function branchRoutePath(branchId: string, path: string): string {
  const rest = normalizeRoutePath(path);
  return `${DRAFT_BRANCH_ROUTE_PREFIX}/${branchId}${rest === "/" ? "" : rest}`;
}

const BRANCH_ID = "[A-Za-z0-9_-]+";

const branchIdPattern = new RegExp(`^${BRANCH_ID}$`);

export function isBranchId(value: string): boolean {
  return branchIdPattern.test(value);
}

const branchRoutePattern = new RegExp(
  `^${DRAFT_BRANCH_ROUTE_PREFIX}/(${BRANCH_ID})(/.*)?$`
);

export function parseBranchRoutePath(
  pathname: string
): { branchId: string; path: string } | null {
  const match = branchRoutePattern.exec(pathname);
  return match ? { branchId: match[1]!, path: match[2] || "/" } : null;
}

// A route accepts OAuth bearers on the membership-gated `space`/`tenant` bars
// (an absent route_auth defaults to the space bar) and on the `sso` bar
// (membership-free external grants). The protected-resource-metadata endpoint
// and the authorization server's resource resolver must agree here, or a route
// advertises OAuth it won't accept — the false-advertisement failure behind
// Tines' one production incident (see notes/tines-mcp-oauth.md), so both go
// through this predicate.
export type OAuthAcceptance = "member" | "external";

export function routeOAuthAcceptance(cfg: {
  routeAuth?: string;
}): OAuthAcceptance | null {
  const bar = cfg.routeAuth ?? "space";
  if (bar === "space" || bar === "tenant") return "member";
  if (bar === "sso") return "external";
  return null;
}

// A branch head is an OAuth resource at exactly one address — main at the bare
// route path on its own bar, a draft at its prefixed path on its floored
// (member) bar — and discovery and minting must agree, so both come here.
export function branchRouteOAuthAcceptance(
  viaBranchPrefix: boolean,
  step: { branchName: string; routeAuth: string }
): OAuthAcceptance | null {
  if (viaBranchPrefix !== (step.branchName !== "main")) return null;
  return routeOAuthAcceptance({
    routeAuth: viaBranchPrefix
      ? draftRouteAuth(step.routeAuth)
      : step.routeAuth,
  });
}

// The resource must carry the space dimension, which lives in the request Host
// (the space subdomain) — not in the fixed public base URL, which collapses
// every space to one origin. Derive the origin from the actual Host so two
// spaces sharing a path get distinct resource identifiers; fall back to the
// configured base URL only when the Host is absent.
export function externalRequestOrigin(
  host: string | null,
  forwardedProto: string | null,
  fallbackBaseUrl: string
): string {
  const fallback = new URL(fallbackBaseUrl);
  if (!host) return fallback.origin;
  const proto = forwardedProto?.split(",")[0]?.trim();
  const scheme =
    proto === "http" || proto === "https"
      ? proto
      : fallback.protocol.slice(0, -1);
  return `${scheme}://${host}`;
}

export const PROTECTED_RESOURCE_CORS_HEADERS = {
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Methods": "GET, OPTIONS",
  "Access-Control-Allow-Headers": "Content-Type",
  "Access-Control-Max-Age": "86400",
} as const;

// RFC 9728 protected-resource metadata document. The platform server passes its
// fixed resource; the public server passes the per-route, host-derived one. Both
// discovery surfaces must advertise the same shape, so they share this builder.
export function protectedResourceMetadataBody(
  resource: string,
  authorizationServer: string
) {
  return {
    resource,
    authorization_servers: [authorizationServer],
    bearer_methods_supported: ["header"],
    scopes_supported: ["mcp"],
  };
}
